import { useMemo } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { QuickFilter } from 'src/hooks/useQuickFilters';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { MARKET_PLACE_TO_CID } from 'src/utils/constants/contentIdMaps';
import {
  DateRangePresetName,
  getDateRangePresets,
} from 'src/utils/dateTimeUtils';
import {
  DateTimeRangeWithRelative,
  Feature,
  Marketplace,
  SaleQuery,
  UserSetting,
} from 'src/WebApiController';

export const useDefaultSaleFilters = ({
  saleQuery,
  initialQuery,
}: {
  saleQuery: SaleQuery;
  initialQuery: SaleQuery;
}) => {
  // Retail is only a selectable marketplace when the RetailSale feature is on
  // (mirrors useSaleFilters), so only surface the Retail quick filter then.
  const hasRetailSaleFeature = useUserHasFeature(Feature.RetailSale);

  const defaultQuickFilters = useMemo(() => {
    const [todayPreset, last30DaysPreset] = getDateRangePresets([
      DateRangePresetName.Today,
      DateRangePresetName.Last30Days,
    ]);
    const filters: QuickFilter<UserSetting.QuickFiltersCustomSales>[] = [
      // Sits immediately after the toolbar-injected "All" chip. Gated on
      // RetailSale so it only appears where Retail is a selectable marketplace.
      ...(hasRetailSaleFeature
        ? [
            {
              id: 'retail',
              name: <Content id={MARKET_PLACE_TO_CID[Marketplace.Retail]} />,
              data: {
                ...initialQuery,
                soldOnMarketplace: Marketplace.Retail,
              },
            },
          ]
        : []),
      {
        id: 'recentlySold',
        name: <Content id={ContentId.RecentlySold} />,
        data: {
          ...initialQuery,
          eventTimeFrameFilter: null,
          saleDates: {
            start: last30DaysPreset.range.start.toISOString(),
            end: last30DaysPreset.range.end.toISOString(),
          } as DateTimeRangeWithRelative,
        },
      },
      {
        id: 'salesToday',
        name: <Content id={ContentId.SalesToday} />,
        data: {
          ...initialQuery,
          eventTimeFrameFilter: null,
          saleDates: {
            start: todayPreset.range.start.toISOString(),
            end: todayPreset.range.end.toISOString(),
          } as DateTimeRangeWithRelative,
        },
      },
    ];

    // Default filters aren't supposed to change view mode
    filters.forEach((f: QuickFilter<UserSetting.QuickFiltersCustomSales>) => {
      f.data.viewMode = saleQuery.viewMode;
    });
    return filters;
  }, [hasRetailSaleFeature, initialQuery, saleQuery.viewMode]);

  return defaultQuickFilters;
};
